import React, { useCallback, useMemo } from 'react'
import { Row, Col, Form, Card } from 'react-bootstrap'
import Button from '@components/common/Button/Button.component'
import CustomColorSelect from './CustomColorSelect.component'
import ProfessionalIdInput from '@components/common/ProfessionalIdInput/ProfessionalIdInput.component'
import ExpiryDateInput from '@components/common/ExpiryDateInput/ExpiryDateInput.component'
import CheckboxDropdown from '@components/layout/Modals/AddPatientModal/CheckboxDropdown'
import {
  DayOfWeek,
  ProviderWorkInfo,
  WorkingSchedule,
} from '@dataTypes/provider.types'
import {
  EmploymentStatus,
  NpiType,
  TaxIdType,
  TIME_OPTIONS,
  US_STATE_OPTIONS,
  WorkStatus,
} from '@constants/Providers'
import { useAppSelector } from '@store/hooks'
import { formatMilitaryTimeTo12Hour } from '@utils/time.util'

type ProviderWorkShiftProps = {
  providerData: ProviderWorkInfo
  handleInputChange: (
    field: string,
    value: string | boolean | number | object,
  ) => void
  onClinicAssignmentChange: (commaSeparatedClinicIds: string) => void
  removeDayOverride: (schedule: WorkingSchedule) => void
  saveNewOverride: () => void
  renderWorkingDays: () => JSX.Element
  showDateOverrideForm: boolean
  currentOverride: WorkingSchedule
  setCurrentOverride: (override: WorkingSchedule) => void
  editingIndex: number | null
  setEditingIndex: (index: number | null) => void
  toggleDateOverrideForm: () => void
  validationErrors?: { [key: string]: string }
  handleShiftTimeChange: (field: string, value: string) => void
}

const ProviderWorkShift: React.FC<ProviderWorkShiftProps> = ({
  providerData,
  handleInputChange,
  onClinicAssignmentChange,
  removeDayOverride,
  saveNewOverride,
  renderWorkingDays,
  showDateOverrideForm,
  currentOverride,
  setCurrentOverride,
  editingIndex,
  setEditingIndex,
  toggleDateOverrideForm,
  validationErrors,
  handleShiftTimeChange,
}) => {
  const operatories = useAppSelector((state) => state.operatory.data)
  const clinics = useAppSelector((state) => state.clinic.data)
  const clinicOptions = clinics.map((clinic) => ({
    clinic_id: clinic.clinic_id,
    name: clinic.clinic_name,
  }))
  const operatoryOptions = operatories.map((operatory) => ({
    operatory_id: operatory.operatory_id,
    name: operatory.name,
    clinic_id: operatory.clinic_id,
  }))

  const clinicCheckboxItems = useMemo(
    () =>
      clinicOptions
        .filter(
          (clinic, index, self) =>
            self.findIndex((c) => c.clinic_id === clinic.clinic_id) === index,
        )
        .map((c) => ({ label: c.name, value: c.clinic_id })),
    [clinicOptions],
  )

  const assignedClinicsCommaValue = useMemo(() => {
    if (providerData.clinicIds && providerData.clinicIds.length > 0) {
      return providerData.clinicIds.join(',')
    }
    return providerData.clinicId || ''
  }, [providerData.clinicIds, providerData.clinicId])

  const assignedClinicIdSet = useMemo(() => {
    const ids =
      providerData.clinicIds && providerData.clinicIds.length > 0
        ? providerData.clinicIds
        : providerData.clinicId
          ? [providerData.clinicId]
          : []
    return new Set(ids)
  }, [providerData.clinicIds, providerData.clinicId])

  const onAssignedClinicsChange = useCallback(
    (next: string): void => {
      onClinicAssignmentChange(next)
    },
    [onClinicAssignmentChange],
  )

  const handleEditOverride = (
    schedule: WorkingSchedule,
    index: number,
  ): void => {
    setEditingIndex(index)
    setCurrentOverride({
      day_of_week: schedule.day_of_week,
      start_time: schedule.start_time,
      end_time: schedule.end_time,
    })
    if (!showDateOverrideForm) {
      toggleDateOverrideForm()
    }
  }

  const handleCancelEdit = (): void => {
    setEditingIndex(null)
    setCurrentOverride({
      day_of_week: DayOfWeek.SUNDAY,
      start_time: '',
      end_time: '',
    })
    toggleDateOverrideForm()
  }

  return (
    <>
      <Row>
        <Col>
          <div className="work-info-numbers">
            <Row className="mb-2 gx-2">
              <Col md={6}>
                <ProfessionalIdInput
                  id="stateLicenseNumber"
                  type="STATE_LICENSE"
                  label="State license number"
                  value={providerData.stateLicenseNumber || ''}
                  onChange={(value) =>
                    handleInputChange('stateLicenseNumber', value)
                  }
                  destructive={!!validationErrors?.stateLicenseNumber}
                />
                {validationErrors?.stateLicenseNumber && (
                  <div className="error-text">
                    {validationErrors.stateLicenseNumber}
                  </div>
                )}
              </Col>
              <Col md={4}>
                <Form.Group controlId="stateWhereLicensed">
                  <Form.Label>State</Form.Label>
                  <Form.Select
                    value={providerData.stateWhereLicensed}
                    onChange={(e) =>
                      handleInputChange('stateWhereLicensed', e.target.value)
                    }
                  >
                    <option value="">Select state</option>
                    {US_STATE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={2}>
                <ExpiryDateInput
                  id="expiryDate"
                  label="Expiry date"
                  value={providerData.expiryDate || ''}
                  onChange={(value) => handleInputChange('expiryDate', value)}
                  destructive={!!validationErrors?.expiryDate}
                  // showError={false}
                  simpleError={true}
                />
                {validationErrors?.expiryDate && (
                  <div className="error-text">
                    {validationErrors.expiryDate}
                  </div>
                )}
              </Col>
            </Row>
            <Row className="mb-2 gx-2">
              <Col md={6}>
                <ProfessionalIdInput
                  id="stateRxId"
                  type="STATE_RX_ID"
                  label="State Rx ID"
                  value={providerData.stateRxId || ''}
                  onChange={(value) => handleInputChange('stateRxId', value)}
                  destructive={!!validationErrors?.stateRxId}
                />
                {validationErrors?.stateRxId && (
                  <div className="error-text">{validationErrors.stateRxId}</div>
                )}
              </Col>
              <Col md={6}>
                <ProfessionalIdInput
                  id="deaNumber"
                  type="DEA"
                  label="DEA number"
                  value={providerData.deaNumber || ''}
                  onChange={(value) => handleInputChange('deaNumber', value)}
                  destructive={!!validationErrors?.deaNumber}
                />
                {validationErrors?.deaNumber && (
                  <div className="error-text">{validationErrors.deaNumber}</div>
                )}
              </Col>
            </Row>
            <Row className="mb-2 gx-2">
              <Col md={6}>
                <Form.Group controlId="taxIdType">
                  <Form.Label>TIN or SSN</Form.Label>
                  <Form.Select
                    value={providerData.taxIdType}
                    onChange={(e) =>
                      handleInputChange('taxIdType', e.target.value)
                    }
                  >
                    <option value="">Select</option>
                    {Object.values(TaxIdType).map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                {providerData.taxIdType === 'TIN' ? (
                  <>
                    <ProfessionalIdInput
                      id="taxIdNumber"
                      type="TAX_ID"
                      label="Tax ID number"
                      value={providerData.taxIdNumber || ''}
                      onChange={(value) =>
                        handleInputChange('taxIdNumber', value)
                      }
                      destructive={!!validationErrors?.taxIdNumber}
                    />
                    {validationErrors?.taxIdNumber && (
                      <div className="error-text">
                        {validationErrors.taxIdNumber}
                      </div>
                    )}
                  </>
                ) : providerData.taxIdType === 'SSN' ? (
                  <>
                    <ProfessionalIdInput
                      id="socialSecurityNumber"
                      type="SSN"
                      label="Social security number"
                      value={providerData.socialSecurityNumber || ''}
                      onChange={(value) =>
                        handleInputChange('socialSecurityNumber', value)
                      }
                      destructive={!!validationErrors?.socialSecurityNumber}
                    />
                    {validationErrors?.socialSecurityNumber && (
                      <div className="error-text">
                        {validationErrors.socialSecurityNumber}
                      </div>
                    )}
                  </>
                ) : null}
              </Col>
            </Row>
            <Row className="mb-2 gx-2">
              <Col md={6}>
                <Form.Group controlId="formNpiType">
                  <Form.Label>NPI type</Form.Label>
                  <Form.Select
                    value={providerData.npiType}
                    onChange={(e) =>
                      handleInputChange('npiType', e.target.value)
                    }
                  >
                    <option value="">Select</option>
                    {Object.values(NpiType).map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <ProfessionalIdInput
                  id="npiNumber"
                  type="NPI"
                  label="NPI number"
                  value={providerData.npiNumber || ''}
                  onChange={(value) => handleInputChange('npiNumber', value)}
                  destructive={!!validationErrors?.npiNumber}
                />
                {validationErrors?.npiNumber && (
                  <div className="error-text">{validationErrors.npiNumber}</div>
                )}
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
      <Row className="mb-2 gx-2">
        <Col md={6}>
          <Form.Group controlId="formClinicIds">
            <Form.Label>Assigned to</Form.Label>
            <div className="add-provider-assigned-dropdown">
              <CheckboxDropdown
                items={clinicCheckboxItems}
                value={assignedClinicsCommaValue}
                onChange={onAssignedClinicsChange}
              />
            </div>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="formEmploymentType">
            <Form.Label>Work position</Form.Label>
            <Form.Select
              value={providerData.employmentType}
              onChange={(e) =>
                handleInputChange('employmentType', e.target.value)
              }
            >
              <option value="">Select</option>
              {Object.values(EmploymentStatus).map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-2 gx-2">
        <Col md={6}>
          <Form.Group controlId="formProviderColor">
            <Form.Label>Staff color</Form.Label>
            <CustomColorSelect
              value={providerData.providerColor}
              onChange={(color) => handleInputChange('providerColor', color)}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-2 gx-2">
        <Col md={6}>
          <Form.Group controlId="formOperatoryRooms">
            <Form.Label>Operatory rooms</Form.Label>
            <Form.Select
              value={providerData.operatoryId}
              onChange={(e) => {
                handleInputChange('operatoryId', e.target.value)
              }}
            >
              <option value="">Select</option>
              {operatoryOptions
                .filter((operatory) =>
                  assignedClinicIdSet.has(operatory.clinic_id),
                )
                .map((operatory) => (
                  <option
                    key={operatory.operatory_id}
                    value={operatory.operatory_id}
                  >
                    {operatory.name}
                  </option>
                ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="formWorkStatus">
            <Form.Label>Work status</Form.Label>
            <Form.Select
              value={providerData.workStatus}
              onChange={(e) => {
                handleInputChange('workStatus', e.target.value)
              }}
            >
              <option value="">Select</option>
              {Object.values(WorkStatus).map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-2 gx-2">
        <Col>
          <Form.Label>Working days</Form.Label>
          {renderWorkingDays()}
        </Col>
        <Col>
          <Row className="gx-2">
            <Col md={6}>
              <Form.Group controlId="shiftStartTime">
                <Form.Label>Shift start</Form.Label>
                <Form.Select
                  value={providerData.shiftStartTime}
                  onChange={(e) =>
                    handleShiftTimeChange('shiftStartTime', e.target.value)
                  }
                >
                  <option value="">Select</option>
                  {TIME_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="shiftEndTime">
                <Form.Label>Shift end</Form.Label>
                <Form.Select
                  value={providerData.shiftEndTime}
                  onChange={(e) =>
                    handleShiftTimeChange('shiftEndTime', e.target.value)
                  }
                >
                  <option value="">Select</option>
                  {TIME_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row className="gx-2">
        <Col>
          <Form.Check
            type="checkbox"
            label="Same working hours"
            checked={providerData.sameWorkingHours}
            onChange={(e) =>
              handleInputChange('sameWorkingHours', e.target.checked)
            }
            className="ps-4 pt-1 pws-check d-flex"
          />
        </Col>
        <Col className="text-end col-add-date-overrides">
          <Button
            size="md"
            className="button-link ado-btn"
            onClick={toggleDateOverrideForm}
            type="button"
          >
            {showDateOverrideForm ? (
              <>
                <span className="ado-btn-desc">Cancel date overrides</span>
              </>
            ) : (
              <>
                <i className="ti ti-plus ado-icon" />{' '}
                <span className="ado-btn-desc">Add date overrides</span>
              </>
            )}
          </Button>
        </Col>
      </Row>
      {showDateOverrideForm && (
        <Row className="mb-2 gx-2">
          <Col md={4}>
            <Form.Group controlId="overrideDate">
              <Form.Label>Working day</Form.Label>
              <Form.Select
                value={currentOverride.day_of_week}
                onChange={(e) =>
                  setCurrentOverride({
                    ...currentOverride,
                    day_of_week: e.target.value as DayOfWeek,
                  })
                }
              >
                <option value="">Select working day</option>
                {Object.values(DayOfWeek)
                  .filter((day) =>
                    providerData.workingSchedules?.some(
                      (schedule) => schedule.day_of_week === day,
                    ),
                  )
                  .map((day) => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group controlId="overrideStartTime">
              <Form.Label>Shift start</Form.Label>
              <Form.Select
                value={currentOverride.start_time}
                onChange={(e) =>
                  setCurrentOverride({
                    ...currentOverride,
                    start_time: e.target.value,
                  })
                }
              >
                <option value="">Select</option>
                {TIME_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group controlId="overrideEndTime">
              <Form.Label>Shift end</Form.Label>
              <Form.Select
                value={currentOverride.end_time}
                onChange={(e) =>
                  setCurrentOverride({
                    ...currentOverride,
                    end_time: e.target.value,
                  })
                }
              >
                <option value="">Select</option>
                {TIME_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2} className="ado-col">
            <label className="select-option-label">&nbsp;</label>
            <Button
              size="md"
              className="button-secondary w-100"
              onClick={saveNewOverride}
              type="button"
            >
              {editingIndex !== null ? 'Update' : 'Add'}
            </Button>
            {editingIndex !== null && (
              <Button
                size="md"
                className="button-link w-100 mt-1"
                onClick={handleCancelEdit}
                type="button"
              >
                Cancel
              </Button>
            )}
          </Col>
        </Row>
      )}
      <div>
        <Row className="gx-2">
          {providerData.workingSchedules
            ?.filter(
              (schedule: WorkingSchedule) =>
                // Only show schedules with valid times
                schedule.start_time &&
                schedule.end_time &&
                schedule.start_time.trim() !== '' &&
                schedule.end_time.trim() !== '' &&
                // Hide schedules that match the shift start and end times
                !(
                  schedule.start_time === providerData.shiftStartTime &&
                  schedule.end_time === providerData.shiftEndTime
                ),
            )
            ?.sort((a: WorkingSchedule, b: WorkingSchedule) => {
              // Define proper day order (Monday to Sunday)
              const dayOrder = [
                DayOfWeek.MONDAY,
                DayOfWeek.TUESDAY,
                DayOfWeek.WEDNESDAY,
                DayOfWeek.THURSDAY,
                DayOfWeek.FRIDAY,
                DayOfWeek.SATURDAY,
                DayOfWeek.SUNDAY,
              ]
              return (
                dayOrder.indexOf(a.day_of_week as DayOfWeek) -
                dayOrder.indexOf(b.day_of_week as DayOfWeek)
              )
            })
            ?.map((schedule: WorkingSchedule, index: number) => (
              <Col md={4} key={index}>
                <Card className="mb-2 gx-2 position-relative date-override-card">
                  <div
                    className="position-absolute d-flex"
                    style={{ top: '5px', right: '5px' }}
                  >
                    <Button
                      size="md"
                      className="button-icon p-0"
                      onClick={() => removeDayOverride(schedule)}
                      type="button"
                    >
                      <i className="ti ti-trash"></i>
                    </Button>
                    <Button
                      size="md"
                      className="button-icon p-0 ms-1"
                      onClick={() => handleEditOverride(schedule, index)}
                      type="button"
                    >
                      <i className="ti ti-pencil"></i>
                    </Button>
                  </div>
                  <Card.Body>
                    <div className="override-date">{schedule.day_of_week}</div>
                    <div className="override-time">
                      <span>
                        {formatMilitaryTimeTo12Hour(schedule.start_time)}
                      </span>
                      <span> - </span>
                      <span>
                        {formatMilitaryTimeTo12Hour(schedule.end_time)}
                      </span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
        </Row>
      </div>
    </>
  )
}

export default ProviderWorkShift
